import React, { useRef, useCallback, useState, useEffect } from 'react'
import { VisLeafletMap, VisLeafletMapRef } from '@unovis/react'
import { LeafletMap, LeafletMapClusterDatum, LeafletMapPoint, LeafletMapPointStyles, Tooltip } from '@unovis/ts'
import { ExampleViewerDurationProps } from '@src/components/ExampleViewer/index'


// Data
import { MapPointDataRecord, points, totalEvents, mapStyleLight, mapStyleDark } from './data'

// Style

export const title = 'Color Map'
export const subTitle = 'Updating color map and data'
export const category = 'Leaflet Map'


export const component = (props: ExampleViewerDurationProps): React.ReactNode => {
  const mapRef = useRef<VisLeafletMapRef<MapPointDataRecord>>(null)
  const [data, setData] = useState(points)
  const [colorMap, setColorMap] = useState<LeafletMapPointStyles<MapPointDataRecord>>({
    normal: { color: '#4c7afc' },
    blocked: { color: '#f8442d' },
  })

  const tooltip = new Tooltip({
    triggers: {
      [LeafletMap.selectors.point]: (d: LeafletMapPoint<MapPointDataRecord>) => {
        return !d.isCluster && !d.clusterPoints ? d.properties?.name : null
      },
    },
    attributes: {
      visLeafletMapTooltipE2eTestId: 'leaflet-map-tooltip',
    },
  })

  const onZoomIn = (): void => { mapRef.current?.component?.zoomIn(1) }
  const onZoomOut = (): void => { mapRef.current?.component?.zoomOut(1) }
  const onFit = (): void => { mapRef.current?.component?.fitView() }

  const pointId = (d: MapPointDataRecord): string => d.name
  const pointLatitude = (d: MapPointDataRecord): number => d.latitude
  const pointLongitude = (d: MapPointDataRecord): number => d.longitude
  const pointBottomLabel = (d: MapPointDataRecord): string => d.name
  const pointRadius = (d: MapPointDataRecord | LeafletMapClusterDatum<MapPointDataRecord>): number =>
    10 + 4 * Math.sqrt(((d.normal || 0) + (d.blocked || 0)) / totalEvents)
  const pointLabel = (d: MapPointDataRecord | LeafletMapClusterDatum<MapPointDataRecord>): string =>
    `${(((d.blocked || 0) + (d.normal || 0)) / 1000).toFixed(1)}K`
  const clusterBottomLabel = (d: LeafletMapClusterDatum<MapPointDataRecord>): string => `${d.point_count} sites`

  // Update color map and data to make sure the map re-renders accordingly.
  // Skipped when `?staticData=1` is present so visual tests get a deterministic view.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('staticData') === '1') return

    // Will be called twice under the dev mode
    const timeoutId = setTimeout(() => {
      // Build a new array instead of mutating the shared imported `points`,
      // which would otherwise accumulate duplicate entries across remounts.
      setData([...points, { latitude: 51.53857, longitude: -0.2520208, name: 'lon-test', normal: 2758, blocked: 642 }])
      setColorMap({
        normal: { color: '#26BDA4' },
        blocked: { color: '#9876AA' },
      })
    }, 5000)

    return () => clearTimeout(timeoutId)
  }, [])


  return (<>
    <VisLeafletMap<MapPointDataRecord>
      ref={mapRef}
      data={data}
      style={mapStyleLight}
      styleDarkTheme={mapStyleDark}
      pointId={useCallback(pointId, [])}
      pointLatitude={useCallback(pointLatitude, [])}
      pointLongitude={useCallback(pointLongitude, [])}
      pointLabel={useCallback(pointLabel, [])}
      pointBottomLabel={useCallback(pointBottomLabel, [])}
      pointRadius={useCallback(pointRadius, [])}
      colorMap={colorMap}
      clusterRadius={useCallback(pointRadius, [])}
      clusterLabel={useCallback(pointLabel, [])}
      clusterBottomLabel={useCallback(clusterBottomLabel, [])}
      clusteringDistance={85}
      clusterExpandOnClick={true}
      tooltip={tooltip}
      duration={props.duration}
      flyToDuration={props.duration}
      zoomDuration={props.duration}
      attributes={{
        [LeafletMap.selectors.point]: {
          cluster: (p: LeafletMapPoint<MapPointDataRecord>) => p.isCluster,
          visLeafletPointE2eTestId: (p: LeafletMapPoint<MapPointDataRecord>) => `leaflet-point-${p.properties?.name}`,
        },
      }}
    />
    <div style={{ position: 'absolute', top: 32, right: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button onClick={onZoomIn}>Zoom In</button>
      <button onClick={onZoomOut}>Zoom Out</button>
      <button onClick={onFit} data-e2e-test-id="leaflet-fit-view">Fit View</button>
    </div>
  </>)
}
